/**
 * @props label: Label above the multi select dropdown (string)
 * @props isOpen: to handle the default open state of the dropdown (boolean)
 * @props searchFieldPlaceholder: placeholder for the input

* @props options: array of objects to render the dropdown items. Object consist of label, value and checked. 
 *          Example: [{ label: "Account payable", value: "1", checked: true }, { label: "Account payable 2", value: "2", checked: false }]
 
* @props suffixProps: Object containing icon or text. Set icon if we want to render the icon and set text if we want to render any text. 
                      suffixProps will set the icon or text at the right side of the text field. suffixProps can also have a callback function for click on icon (suffixIconCb: () => {})
                      Example: { icon: VEC_ICON_NAME.ICON_DROPDOWN_CARET } or { text: "+91" } or { prefixIconCb: () => { } }
 * 
 * @props prefixProps: Object containing icon or text. Set icon if we want to render the icon and set text if we want to render any text. 
                      prefixProps will set the icon or text at the left side of the text field. prefixProps can also have a callback function for click on icon (prefixIconCb: () => {})
                      Example: { icon: VEC_ICON_NAME.ICON_DROPDOWN_CARET } or { text: "+91" } or { prefixIconCb: () => { } }

    @props FooterComponent: React Component
 */

import React, { useEffect, useRef, useState } from "react";
import CheckboxWLabel from "../../checkbox/checkboxwlabel/CheckboxWLabel";
import { VEC_ICON_NAME } from "../../icons/constants";
import styles from "./MultiSelectDropdown.styles"
import TextInput from "../../input/textinput/TextInput";

export default function MultiSelectDropdown({
    label = "",
    options = [],
    isOpen = false,
    suffixProps = { icon: VEC_ICON_NAME.SEARCH_ICON },
    prefixProps = {},
    dropdownOptionsCustomStyles = {},
    FooterComponent = null,
    searchFieldValue = "",
    searchFieldPlaceholder = "Search...",
    searchFieldChangeCb = () => { },
    onSelectOptionsCb = () => { },
    maxSelection = null,
    tabIndex = "0",
    dropdownSingleOptionContainerCustomStyles = {},
    disabled,
    isSelectAllDisabled = false,
}) {
    const [isDropdownOpen, setIsDropdownOpen] = useState(isOpen);
    const [dropdownOptions, setDropdownOptions] = useState(options);
    const [topInputValue, setTopInputValue] = useState("");
    const [searchInputValue, setSearchInputValue] = useState(searchFieldValue);
    const [dropdownPosition, setDropdownPosition] = useState({
        left: 0,
        right: 'auto',
    });
    const [filteredOptions, setFilteredOptions] = useState(options);

    const wrapperRef = useRef(null);

    useEffect(() => {
        setDropdownOptions(options);
        setFilteredOptions(options);
    }, [options])

    useEffect(() => {
        const newOptions = filteredOptions.map((option, i) => {
            const findItemFromDropdownOptions = dropdownOptions.find(item => item?.value === option?.value)
            if (findItemFromDropdownOptions) {
                return {
                    ...option,
                    checked: findItemFromDropdownOptions?.checked ?? false
                }
            } else {
                return option
            }
        });
        setFilteredOptions(newOptions);
    }, [dropdownOptions])

    useEffect(() => {
        window.addEventListener('click', handleClickOutside);
        return () => {
            window.removeEventListener('click', handleClickOutside);
        };
    }, []);

    useEffect(() => {
        const calculateDropdownPosition = () => {
            const wrapper = wrapperRef.current;
            if (wrapper) {
                const rect = wrapper.getBoundingClientRect();
                const windowWidth = window.innerWidth;

                if (windowWidth - rect.right < rect.width) {
                    setDropdownPosition({ left: 'auto', right: 0 });
                } else {
                    setDropdownPosition({ left: 0, right: 'auto' });
                }
            }
        };

        calculateDropdownPosition();

        window.addEventListener('resize', calculateDropdownPosition);

        return () => {
            window.removeEventListener('resize', calculateDropdownPosition);
        };
    }, []);

    const handleMultiSelectOptionChange = (selectedOption) => {
        const newOptions = dropdownOptions.map((option, i) => {
            if (option?.value !== selectedOption?.value) {
                return option;
            }

            if (option.checked || maxSelection === null || countSelectedOptions(dropdownOptions) < maxSelection) {
                return {
                    ...option,
                    checked: !option.checked,
                };
            }

            return option;
        });

        setDropdownOptions(newOptions);
        onSelectOptionsCb(newOptions.filter(item => item.checked));
        updateTopInputValue(newOptions)
    };

    const handleClickOutside = (e) => {
        if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
            setIsDropdownOpen(false);
        }
    }

    const countSelectedOptions = (options) => {
        return options.filter(option => option.checked).length;
    };

    const handleSearchInputChange = (event) => {
        const value = event.target.value;
        setSearchInputValue(value);
        setFilteredOptions(dropdownOptions.filter(option => option.label.toLowerCase().includes(value.toLowerCase())));
        searchFieldChangeCb(value);
    };

    const handleSelectAllChange = () => {
        const allChecked = filteredOptions.length > 0 && filteredOptions.every(option => option.checked);
        const updatedOptions = dropdownOptions.map(option =>
            filteredOptions.some(filteredOption => filteredOption?.value === option?.value)
                ? { ...option, checked: !allChecked }
                : option
        );

        setDropdownOptions(updatedOptions);
        onSelectOptionsCb(updatedOptions.filter(item => item.checked));
        updateTopInputValue(updatedOptions)
    };

    const updateTopInputValue = (options) => {
        const selectedOptions = options.filter(option => option.checked);
        const selectedCount = selectedOptions.length;

        if (selectedCount < 3) {
            setTopInputValue(selectedOptions.map(option => option.label).join(', '));
        } else {
            setTopInputValue(`${selectedCount} Selected`);
        }
    };

    return (
        <>
            <div ref={wrapperRef} style={styles.container}>

                <TextInput
                    label={label}
                    value={topInputValue}
                    suffixProps={suffixProps}
                    onClickCb={() => setIsDropdownOpen(prev => !prev)}
                    customStyles={styles.textInput}
                    onChangeCb={searchFieldChangeCb}
                    tabIndex={tabIndex}
                    disabled={disabled}
                />


                {isDropdownOpen && (
                    <>
                        <div
                            style={{ ...styles.dropdownOptionsContainer, ...dropdownOptionsCustomStyles, ...dropdownPosition }}
                        >
                            <div style={styles.selectAllAndSearchContainer}>
                                <div style={styles.selectAllContainer}>
                                    <CheckboxWLabel
                                        checked={filteredOptions.length > 0 && filteredOptions.every(option => option.checked)}
                                        label=""
                                        onChangeCb={handleSelectAllChange}
                                        containerCustomStyle={dropdownSingleOptionContainerCustomStyles}
                                        disabled={isSelectAllDisabled ? filteredOptions.some(item => item.disabled) : false}
                                    />
                                </div>
                                <div style={styles.searchInputContainer}>
                                    <TextInput
                                        value={searchInputValue}
                                        onChangeCb={handleSearchInputChange}
                                        placeHolder={searchFieldPlaceholder}
                                        customStyles={styles.searchInput}
                                        tabIndex={tabIndex}
                                        customInputContainerStyles={styles.customInputContainerStyles}
                                    />
                                </div>
                            </div>
                            <div style={styles.scrollableBody}>
                                {filteredOptions.map((option, index) => (
                                    <div key={`${option.label}-${index}`} style={styles.checkBoxContainer}>
                                        <CheckboxWLabel
                                            checked={option.checked}
                                            label={option.label}
                                            onChangeCb={() => handleMultiSelectOptionChange(option)}
                                            containerCustomStyle={dropdownSingleOptionContainerCustomStyles}
                                            disabled={option?.disabled}
                                        />
                                    </div>
                                ))}
                            </div>

                            {FooterComponent}
                        </div>
                    </>
                )}
            </div>
        </>
    );
}